import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SettingsModal } from "src/app/SettingsModal/SettingsModal";
import {
  SettingsContext,
  ProjectSettingsTab,
} from "src/app/SettingsModal/enums";
import { openSettingsModal } from "src/app/SettingsModal/methods";
import { AutomationsSection } from "src/app/SettingsModal/tabs/Automations/Automations";
import { ExtensionsSection } from "src/app/SettingsModal/tabs/Extensions/Extensions";
import { RootState } from "src/redux/reducers";
import { IProject } from "src/redux/project/types";
import {
  ProjectPermissionsEnum,
  WorkspacePermissionsEnum,
} from "src/utils/PermissionsEnums";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "../../../components/Dropdown/Dropdown";
import DropdownPortal from "../../../components/DropdownPortal";
import { ReactComponent as CustomizeIcon } from "src/images/settings-alt.svg";
import { HIDDEN_FIELDS } from "src/utils/variables";
import ConfirmationOfDeleteProjectModal from "../../../modals/ConfirmationOfDeleteProjectModal/ConfirmationOfDeleteProjectModal";
import DuplicateProjectModal from "../../../modals/DuplicateProjectModal/DuplicateProjectModal";
import { SeeAsClientInfoModal } from "../../../modals/ShareModal/SeeAsClientInfoModal/SeeAsClientInfoModal";
import { RolesInfoModal } from "../../../modals/ShareModal/RolesInfoModal/RolesInfoModal";
import { copyProjectOrTaskLink } from "../../../methods/copyProjectOrTaskLink";
import { Button } from "../../../components/Button/Button";

import { ReactComponent as CopyLinkIcon } from "../../../../images/link.svg";
import { ReactComponent as LighteningIcon } from "../../../../images/dots.svg";
import { ReactComponent as DuplicateIcon } from "../../../../images/duplicate.svg";
import { ReactComponent as DeleteIcon } from "../../../../images/trash-can.svg";
import { ReactComponent as SettingsIcon } from "../../../../images/settings.svg";
import { ReactComponent as WandIcon } from "../../../../images/wand.svg";
import { ReactComponent as SwitchIcon } from "../../../../images/switch.svg";
import { ReactComponent as PlugIcon } from "../../../../images/plug.svg";
import {
  setEditedAutomationData,
  setShowPublicationsDetectedModal,
} from "../../../../redux/automation/automationActions";
import { PublicationsDetectedModal } from "../../../modals/PublicationsDetectedModal/PublicationsDetectedModal";
import useCheckProjectPermission from "src/hooks/useCheckProjectPermission";
import { tableDataType } from "src/app/components/Table/Table";
import CustomizeFieldsDropdown from "src/app/dropdowns/CustomizeFieldsDropdown/CustomizeFieldsDropdown";
import ManageFieldModal from "src/app/modals/ManageFieldModal/ManageFieldModal";

interface ProjectMenuDropdownProps {
  data: IProject;
  singleProject?: boolean;
  showCreatorSearchTab: boolean;
}

export function ProjectMenuDropdown(props: ProjectMenuDropdownProps) {
  const { data, singleProject, showCreatorSearchTab } = props;

  const [showDuplicateProjectModal, setShowDuplicateProjectModal] =
    useState<boolean>(false);
  const [
    showConfirmationOfDeleteProjectModal,
    setShowConfirmationOfDeleteProjectModal,
  ] = useState<boolean>(false);
  const [showSeeAsClientInfoModal, setShowSeeAsClientInfoModal] =
    useState(false);
  const [showRulesInfoModal, setShowRolesInfoModal] = useState(false);
  const [editedFieldData, setEditedFieldData] = useState<any>({});
  const [showProjectFieldSetupModal, setShowProjectFieldSetupModal] =
    useState(false);
  const [menuVisible, setMenuVisible] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const {
    mainReducer: { activeWorkspaceUuid, settingsModalData, identity },
    automationReducer: {
      editedAutomationData,
      automationList,
      showPublicationsDetectedModal,
    },
  } = useSelector((state: RootState) => state);

  const { projectMetaFields, taskType } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const hasProjectMetaFieldsManagementPermission = useCheckProjectPermission(
    "PROJECT_META_FIELDS_MANAGEMENT",
  );

  const renderCustomizeFieldsDropdown = () => {
    if (!hasProjectMetaFieldsManagementPermission || showCreatorSearchTab)
      return null;

    return (
      <CustomizeFieldsDropdown
        setShowFieldSetupModal={setShowProjectFieldSetupModal}
        fieldData={editedFieldData}
        setFieldData={setEditedFieldData}
        dropdownButton={
          <DropdownMenuItem data-qa="overview-settings-button">
            <CustomizeIcon />
            <IDHFormattedMessage
              id="ws_overview_settings"
              defaultMessage="Overview settings"
            />
          </DropdownMenuItem>
        }
        fields={projectMetaFields
          ?.filter((field) =>
            projectMetaFields?.find(
              (metafield) =>
                metafield.uuid === field.uuid &&
                metafield.taskType === taskType &&
                !HIDDEN_FIELDS.includes(field.key),
            ),
          )
          ?.map((field) => ({
            ...field,
            title: field.name,
          }))}
        dataType={tableDataType.Project}
        title={
          <IDHFormattedMessage
            id="ws_overview_settings"
            defaultMessage="Overview settings"
          />
        }
        subheader={
          <IDHFormattedMessage id="ws_overview" defaultMessage="Overview" />
        }
      />
    );
  };

  const onCloseDuplicateProjectModal = () => {
    setShowDuplicateProjectModal(false);
  };

  const handleOpenSettingsModal = (
    tab: ProjectSettingsTab,
    section?: string,
  ) => {
    setMenuVisible(false);

    openSettingsModal(
      history,
      `project-menu-dropdown-${data.projectId}`,
      SettingsContext.Project,
      tab,
      section,
    );
  };

  useEffect(() => {
    if (!editedAutomationData.uuid) return;

    const newAutomationData = automationList.find(
      (item) => item.uuid === editedAutomationData.uuid,
    );

    dispatch(setEditedAutomationData(newAutomationData ?? {}));
  }, [automationList]);

  const handleCopyProjectLink = () => {
    setMenuVisible(false);
    copyProjectOrTaskLink(activeWorkspaceUuid, data.projectId);
  };

  const handleOpenDuplicateProjectModal = () => {
    setMenuVisible(false);
    setShowDuplicateProjectModal(true);
  };

  const handleOpenConfirmationOfDeleteProjectModal = () => {
    setMenuVisible(false);
    setShowConfirmationOfDeleteProjectModal(true);
  };

  return (
    <>
      <DropdownPortal
        overlay={
          <DropdownMenu isOpen>
            {renderCustomizeFieldsDropdown()}

            <DropdownMenuItem
              onClick={() =>
                handleOpenSettingsModal(ProjectSettingsTab.General)
              }
              data-qa="settings-button"
            >
              <SwitchIcon />
              <IDHFormattedMessage id="ws_settings" defaultMessage="Settings" />
            </DropdownMenuItem>
            {data.permissions?.project?.includes(
              "display_project_configuration",
            ) ? (
              <>
                <DropdownMenuItem
                  onClick={() =>
                    handleOpenSettingsModal(
                      ProjectSettingsTab.Automations,
                      AutomationsSection.AutomationsMain,
                    )
                  }
                  data-qa="automations-button"
                >
                  <WandIcon />
                  <IDHFormattedMessage
                    id="ws_automations"
                    defaultMessage="Automations"
                  />
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    handleOpenSettingsModal(
                      ProjectSettingsTab.Extensions,
                      ExtensionsSection.ExtensionsList,
                    )
                  }
                  data-qa="extensions-button"
                >
                  <PlugIcon />
                  <IDHFormattedMessage
                    id="ws_extensions"
                    defaultMessage="Extensions"
                  />
                </DropdownMenuItem>
              </>
            ) : null}

            <DropdownMenuItem
              onClick={handleCopyProjectLink}
              data-qa="copy-link-button"
            >
              <CopyLinkIcon />
              <IDHFormattedMessage
                id="ws_copy_link"
                defaultMessage="Copy link"
              />
            </DropdownMenuItem>

            {data.permissions?.project.includes(
              ProjectPermissionsEnum.PROJECT_MANAGEMENT,
            ) && (
              <>
                {identity.permissions?.workspace.includes(
                  WorkspacePermissionsEnum.CREATE_PROJECTS,
                ) && (
                  <DropdownMenuItem
                    onClick={handleOpenDuplicateProjectModal}
                    data-qa="duplicate-button"
                  >
                    <DuplicateIcon />
                    <IDHFormattedMessage
                      id="ws_duplicate"
                      defaultMessage="Duplicate"
                    />
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="dropdown__menu-item--danger"
                  onClick={handleOpenConfirmationOfDeleteProjectModal}
                  data-qa="remove-button"
                >
                  <DeleteIcon />
                  <IDHFormattedMessage id="ws_remove" defaultMessage="Remove" />
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenu>
        }
        placement="bottomRight"
        autoAdjustOverflow
        onVisibleChange={(visible: boolean) => setMenuVisible(visible)}
        visible={menuVisible}
      >
        <span className="project-menu-dropdown">
          {singleProject ? (
            <Button
              variant="light-grey"
              size="square-small"
              className="project-menu-dropdown__menu-icon"
              data-qa="campaign-dropdown-button"
            >
              <SettingsIcon />
            </Button>
          ) : (
            <Button
              className="projects-list__menu-button"
              variant="light"
              size="large"
              data-qa="campaign-dropdown-button"
            >
              <LighteningIcon />
            </Button>
          )}
        </span>
      </DropdownPortal>

      {settingsModalData.location ===
        `project-menu-dropdown-${data.projectId}` && (
        <SettingsModal
          members={{
            openShareModal: () =>
              handleOpenSettingsModal(ProjectSettingsTab.Members),
            openSeeAsClientInfoModal: () => setShowSeeAsClientInfoModal(true),
            openRolesInfoModal: () => setShowRolesInfoModal(true),
          }}
          contextTargetUuid={data.projectId}
        />
      )}

      {showPublicationsDetectedModal ? (
        <PublicationsDetectedModal
          onClose={() => dispatch(setShowPublicationsDetectedModal(false))}
        />
      ) : null}

      {showDuplicateProjectModal && (
        <DuplicateProjectModal
          wsProjectUuid={data.projectId}
          projectName={data.projectName}
          onClose={() => onCloseDuplicateProjectModal()}
        />
      )}

      {showConfirmationOfDeleteProjectModal && (
        <ConfirmationOfDeleteProjectModal
          wsProjectUuid={data.projectId}
          wsProjectName={data.projectName}
          wsWorkspaceUuid={activeWorkspaceUuid}
          onClose={() => setShowConfirmationOfDeleteProjectModal(false)}
          singleProject={singleProject}
        />
      )}

      {showSeeAsClientInfoModal && (
        <SeeAsClientInfoModal
          onClose={() => setShowSeeAsClientInfoModal(false)}
          openShareModal={() =>
            handleOpenSettingsModal(ProjectSettingsTab.Members)
          }
        />
      )}

      {showRulesInfoModal && (
        <RolesInfoModal
          onClose={() => setShowRolesInfoModal(false)}
          openShareModal={() =>
            handleOpenSettingsModal(ProjectSettingsTab.Members)
          }
        />
      )}

      {showProjectFieldSetupModal && (
        <ManageFieldModal
          data={editedFieldData}
          onClose={() => setShowProjectFieldSetupModal(false)}
          dataType={tableDataType.Project}
          projectId={data.projectId}
        />
      )}
    </>
  );
}
